import React from 'react';
import { ApolloError } from '@apollo/client';
import { useSnackbar } from 'pounceV2';
import { extractErrorMessage } from 'Helpers/utils';
import useUrlParams from 'Hooks/useUrlParams';
import useCopyToClipboardV3 from 'Hooks/useCopyToClipboardV3';
import { useUpdateAIConversation } from '../graphql/updateAIConversation.generated';
import { ConversationalAIParams } from '../types';

interface UseConversationManagementProps {
  conversationId: string | null;
  isPrivate: boolean;
  canEdit: boolean;
  cancelStream: () => void;
  onReset: () => void;
}

interface UseConversationManagementReturn {
  handleNewPrompt: () => void;
  handleCopyLink: () => void;
  handleDelete: () => void;
  handleTogglePrivacy: () => void;
  canEdit: boolean;
  isLoading: boolean;
}

/**
 * Hook for managing AI conversation lifecycle actions
 */
export const useConversationManagementV3 = ({
  conversationId,
  isPrivate,
  canEdit,
  cancelStream,
  onReset,
}: UseConversationManagementProps): UseConversationManagementReturn => {
  const { pushSnackbar } = useSnackbar();
  const { updateUrlParams } = useUrlParams<ConversationalAIParams>();
  const copyToClipboard = useCopyToClipboardV3();

  const [updateConversation, { loading: updateLoading }] = useUpdateAIConversation();

  const handleNewPrompt = React.useCallback(() => {
    cancelStream();
    updateUrlParams({ conversationId: undefined });
    onReset();
  }, [cancelStream, updateUrlParams, onReset]);

  const handleCopyLink = React.useCallback(() => {
    if (conversationId) {
      const params = new URLSearchParams({ conversationId });
      const shareableUrl = `${window.location.origin}${
        window.location.pathname
      }?${params.toString()}`;
      copyToClipboard(shareableUrl, {
        variant: 'success',
        title: 'Link copied to clipboard',
        duration: 1500,
      });
    }
  }, [conversationId, copyToClipboard]);

  const handleTogglePrivacy = React.useCallback(() => {
    if (!conversationId) {
      return;
    }

    updateConversation({
      variables: {
        input: {
          conversationId,
          private: !isPrivate,
        },
      },
      onCompleted: () => {
        pushSnackbar({
          variant: 'success',
          title: `Chat is now ${!isPrivate ? 'private' : 'shared'}`,
        });
      },
      onError: error => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to update privacy',
          description: extractErrorMessage(error),
        });
      },
      refetchQueries: ['AIConversation', 'AIConversations'],
      awaitRefetchQueries: true,
    });
  }, [conversationId, isPrivate, updateConversation, pushSnackbar]);

  const handleDelete = React.useCallback(async () => {
    if (!conversationId) {
      return;
    }

    // Cancel any active stream before deleting
    cancelStream();

    try {
      await updateConversation({
        variables: {
          input: {
            conversationId,
            deleted: true,
          },
        },
        refetchQueries: ['AIConversations'],
        awaitRefetchQueries: true,
      });
      // Runs AFTER refetches complete — button cache is now updated
      updateUrlParams({ conversationId: undefined });
      onReset();
      pushSnackbar({
        variant: 'success',
        title: 'Conversation deleted',
      });
    } catch (error) {
      pushSnackbar({
        variant: 'error',
        title: 'Failed to delete conversation',
        description: extractErrorMessage(error as ApolloError),
      });
    }
  }, [conversationId, cancelStream, updateConversation, pushSnackbar, updateUrlParams, onReset]);

  return {
    handleNewPrompt,
    handleCopyLink,
    handleDelete,
    handleTogglePrivacy,
    canEdit,
    isLoading: updateLoading,
  };
};
